import React, { useEffect } from "react";
import { Layout, Menu, Tooltip, Drawer } from "antd";
import { useLocation } from "react-router-dom";
import logo from "../../assets/images/image.png";
import { menuItems, filterByRoles, getSelectedKey } from "../SidebarMenuUtils";
import { useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobile } from "../../hooks/useIsMobile";

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  mobileOpen: boolean;
  setMobileOpen: (value: boolean) => void;
  SIDEBAR_WIDTH: number;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  setCollapsed,
  mobileOpen,
  setMobileOpen,
  SIDEBAR_WIDTH,
}) => {
  const userProfileInfo = useAppSelector((state) => state.auth.userProfileData);
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) return;
    const handleResize = () => {
      const isNarrow = window.innerWidth < 1200;
      setCollapsed(isNarrow);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile, setCollapsed]);

  // Close mobile drawer after navigation
  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile, setMobileOpen]);

  const filteredItems = filterByRoles(menuItems, userProfileInfo?.roles || []);
  const selectedKey = getSelectedKey(location.pathname);

  const brandHeader = (compact: boolean) => (
    <div
      style={{
        flex: "0 0 70px",
        display: "flex",
        alignItems: "center",
        justifyContent: compact ? "center" : "flex-start",
        padding: compact ? 0 : "0 20px",
        background: "rgba(255,255,255,0.8)",
        borderBottom: "1px solid #f0f0f0",
        backdropFilter: "blur(8px)",
      }}
    >
      <Tooltip title="NeoView" placement="right">
        <img
          src={logo}
          alt="logo"
          style={{
            height: 32,
            marginRight: compact ? 0 : 12,
            transition: "all 0.3s ease",
          }}
        />
      </Tooltip>
      {!compact && (
        <span
          style={{
            fontWeight: 700,
            fontSize: 18,
            background: "linear-gradient(90deg, #1677fa 0%, #52c41a 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          NeoView
        </span>
      )}
    </div>
  );

  const navMenu = (inlineCollapsed: boolean) => (
    <Menu
      mode="inline"
      inlineCollapsed={inlineCollapsed}
      triggerSubMenuAction={isMobile ? "click" : "hover"}
      items={filteredItems}
      selectedKeys={[selectedKey]}
      style={{
        border: "none",
        background: "transparent",
      }}
      theme="light"
    />
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        width={SIDEBAR_WIDTH + 24}
        styles={{
          body: { padding: 0, display: "flex", flexDirection: "column", height: "100%" },
          header: { display: "none" },
        }}
        style={{ zIndex: 1100 }}
      >
        {brandHeader(false)}
        <div
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            padding: "12px",
          }}
        >
          {navMenu(false)}
        </div>
      </Drawer>
    );
  }

  return (
    <Sider
      width={SIDEBAR_WIDTH}
      collapsible
      collapsed={collapsed}
      trigger={null}
      style={{
        position: "fixed",
        left: 16,
        top: 16,
        bottom: 16,
        height: "calc(100vh - 32px)",
        overflow: "hidden",
        background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
        borderRight: "1px solid #f0f0f0",
        zIndex: 900,
        borderRadius: 16,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        transition: "all 0.3s ease",
      }}
    >
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {brandHeader(collapsed)}

        <div
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            padding: collapsed ? "8px 6px" : "12px 12px",
          }}
        >
          {navMenu(collapsed)}
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => setCollapsed(!collapsed)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setCollapsed(!collapsed);
          }}
          style={{
            flex: "0 0 auto",
            cursor: "pointer",
            padding: collapsed ? "10px 0" : "10px 16px",
            textAlign: collapsed ? "center" : "left",
            color: "#6b7280",
            fontSize: 13,
            borderTop: "1px solid #f0f0f0",
            background: "rgba(255,255,255,0.95)",
            transition: "all 0.3s ease",
            fontWeight: 500,
            userSelect: "none",
          }}
        >
          {collapsed ? "→ Expand" : "← Collapse"}
        </div>
      </div>
    </Sider>
  );
};

export default Sidebar;
